package dev.lingvo.site.api

import dev.lingvo.shared.ApiErrorBody
import dev.lingvo.site.i18n.tr
import kotlinx.serialization.json.JsonObject
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.files.Blob

@PublishedApi
internal suspend inline fun <reified ResponseBody> jsonRequest(
    method: String,
    path: String,
    authenticated: Boolean = true,
    timeoutMs: Long? = null,
): ResponseBody {
    if (timeoutMs == null) {
        val response = apiRequest(method, path, authenticated = authenticated)
        val rawBody = responseText(response, path)
        return try {
            API_JSON.decodeFromString(rawBody)
        } catch (failure: Throwable) {
            throw invalidResponse(path, failure)
        }
    }
    val controller: dynamic = js("typeof AbortController === 'undefined' ? null : new AbortController()")
    return try {
        withTimeout(timeoutMs) {
            val response = apiRequestWithAbort(method, path, authenticated, controller?.signal)
            val rawBody = responseText(response, path)
            try {
                API_JSON.decodeFromString<ResponseBody>(rawBody)
            } catch (failure: Throwable) {
                throw invalidResponse(path, failure)
            }
        }
    } catch (_: TimeoutCancellationException) {
        controller?.abort()
        throw ApiException(
            status = 0,
            code = "request_timeout",
            message = tr(
                "ds.api.api_transport.couldnt_check_your_saved_session_check",
                "Couldn't check your saved session. Check your connection and retry.",
            ),
        )
    } catch (cancelled: CancellationException) {
        controller?.abort()
        throw cancelled
    }
}

@PublishedApi
internal suspend fun apiRequestWithAbort(
    method: String,
    path: String,
    authenticated: Boolean,
    signal: dynamic,
): Response {
    val headers = Headers().apply {
        set("Accept", JSON_CONTENT_TYPE)
        if (authenticated) {
            Session.token?.takeIf(String::isNotBlank)?.let { set("Authorization", "Bearer $it") }
        }
    }
    val request = RequestInit(method = method, headers = headers)
    if (signal != null) request.asDynamic().signal = signal
    val response = try {
        window.fetch("${ApiConfig.baseUrl}$path", request).await()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        throw ApiException(
            status = 0,
            code = "network_error",
            message = failure.message?.takeIf(String::isNotBlank)
                ?: "Couldn't reach the Lingvo server.",
        )
    }
    if (!response.ok) throw response.toApiException()
    return response
}

@PublishedApi
internal suspend inline fun <reified RequestBody, reified ResponseBody> jsonRequest(
    method: String,
    path: String,
    body: RequestBody,
    authenticated: Boolean = true,
    headers: Map<String, String> = emptyMap(),
): ResponseBody = jsonRequest(
    method = method,
    path = path,
    body = API_JSON.encodeToString(body),
    authenticated = authenticated,
    headers = headers,
)

@PublishedApi
internal suspend inline fun <reified ResponseBody> jsonRequest(
    method: String,
    path: String,
    body: String,
    authenticated: Boolean,
    headers: Map<String, String> = emptyMap(),
): ResponseBody {
    val response = apiRequest(method, path, body, authenticated, extraHeaders = headers)
    val rawBody = responseText(response, path)
    return try {
        API_JSON.decodeFromString(rawBody)
    } catch (failure: Throwable) {
        throw invalidResponse(path, failure)
    }
}

internal suspend fun unitRequest(method: String, path: String) {
    apiRequest(method, path)
}

/** For endpoints that take a body and answer 204 — a role change, for instance. */
@PublishedApi
internal suspend inline fun <reified RequestBody> unitRequest(
    method: String,
    path: String,
    body: RequestBody,
) {
    apiRequest(method, path, API_JSON.encodeToString(body))
}

internal suspend fun textRequest(
    method: String,
    path: String,
    accept: String,
): String = responseText(apiRequest(method, path, accept = accept), path)

/**
 * Sends a file exactly as the reader chose it, with no envelope.
 *
 * A raw body rather than multipart or base64-in-JSON. Multipart would need a parser on both sides
 * for one field, and base64 inflates the bytes by a third against a 256KB ceiling. The server reads
 * the stream and proves the type from the magic number, so nothing about the request needs to
 * describe the content — which is the point: a `Content-Type` a client sets is a claim, and this
 * endpoint is careful not to trust one.
 */
internal suspend fun binaryRequest(method: String, path: String, body: Blob) {
    val headers = Headers().apply {
        set("Accept", JSON_CONTENT_TYPE)
        Session.token?.takeIf(String::isNotBlank)?.let { set("Authorization", "Bearer $it") }
    }
    val response = try {
        window.fetch(
            "${ApiConfig.baseUrl}$path",
            RequestInit(method = method, headers = headers, body = body),
        ).await()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        throw ApiException(
            status = 0,
            code = "network_error",
            message = failure.message?.takeIf(String::isNotBlank)
                ?: "Couldn't reach the Lingvo server.",
        )
    }
    if (!response.ok) throw response.toApiException()
}

internal suspend fun blobRequest(
    method: String,
    path: String,
    accept: String,
): Blob {
    val response = apiRequest(method, path, accept = accept)
    return try {
        response.blob().await()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        throw invalidResponse(path, failure)
    }
}

@PublishedApi
internal suspend fun apiRequest(
    method: String,
    path: String,
    body: String? = null,
    authenticated: Boolean = true,
    accept: String = JSON_CONTENT_TYPE,
    /** Extra headers. Used for `If-Match`, which is per-call rather than per-session. */
    extraHeaders: Map<String, String> = emptyMap(),
): Response {
    val headers = Headers().apply {
        set("Accept", accept)
        if (body != null) set("Content-Type", JSON_CONTENT_TYPE)
        if (authenticated) {
            Session.token?.takeIf(String::isNotBlank)?.let { set("Authorization", "Bearer $it") }
        }
        extraHeaders.forEach { (name, value) -> set(name, value) }
    }
    val response = try {
        window.fetch(
            "${ApiConfig.baseUrl}$path",
            RequestInit(method = method, headers = headers, body = body),
        ).await()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        throw ApiException(
            status = 0,
            code = "network_error",
            message = failure.message?.takeIf(String::isNotBlank)
                ?: "Couldn't reach the Lingvo server.",
        )
    }
    if (!response.ok) throw response.toApiException()
    return response
}

private suspend fun Response.toApiException(): ApiException {
    val rawBody = try {
        text().await()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (_: Throwable) {
        ""
    }
    val decoded = runCatching { API_JSON.decodeFromString<ApiErrorBody>(rawBody).error }.getOrNull()
    val responseStatus = status.toInt()
    return ApiException(
        status = responseStatus,
        details = decoded?.details ?: JsonObject(emptyMap()),
        code = decoded?.code ?: "http_$responseStatus",
        // **Only a message the API deliberately wrote. Never the raw body.**
        //
        // This used to fall through to `rawBody` whenever the response was not a structured Lingvo
        // error — which is exactly when the body is least safe to show. A failing request can return
        // a proxy's HTML error page, a gateway's stack trace, a mail provider's reply, or a
        // Cloudflare interstitial, and every one of those went straight to a pixel through whichever
        // screen happened to render `exception.message`. Fifteen call sites bind that string.
        //
        // Fixing it at those fifteen sites would have been fifteen chances to forget. Fixing it here
        // means a caller *cannot* render a raw body: `decoded.message` is a sentence the API chose
        // to write for a person, and everything else collapses to one of ours. Structured errors —
        // "Locale 'xx' is not enabled for this project" — still reach the screen unchanged, which is
        // the whole point of having them.
        //
        // `statusText` goes too. "Bad Gateway" tells a translator nothing they can act on, and
        // reads as though the product is describing itself in someone else's words.
        message = decoded?.message ?: genericFailure(responseStatus),
    )
}

@PublishedApi
internal suspend fun responseText(response: Response, path: String): String = try {
    response.text().await()
} catch (cancelled: CancellationException) {
    throw cancelled
} catch (failure: Throwable) {
    throw invalidResponse(path, failure)
}

@PublishedApi
internal fun invalidResponse(path: String, cause: Throwable): ApiException = ApiException(
    status = INVALID_RESPONSE_STATUS,
    code = "invalid_response",
    message = "The server returned an invalid response for $path." +
        cause.message?.takeIf(String::isNotBlank)?.let { " $it" }.orEmpty(),
)

internal fun segment(value: String): String = encodeURIComponent(value)

internal fun query(vararg parameters: Pair<String, String?>): String {
    val present = parameters.filter { it.second != null }
    if (present.isEmpty()) return ""
    return present.joinToString(prefix = "?", separator = "&") { (name, value) ->
        "${encodeURIComponent(name)}=${encodeURIComponent(value.orEmpty())}"
    }
}

@Serializable
internal data class JobCreatedResponse(val jobId: String)

internal const val TEXT_CONTENT_TYPE = "text/plain"
internal const val ZIP_CONTENT_TYPE = "application/zip"
private const val JSON_CONTENT_TYPE = "application/json"
private const val INVALID_RESPONSE_STATUS = 502

val API_JSON = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
    explicitNulls = false
}

private external fun encodeURIComponent(value: String): String

/**
 * What we say when the server did not say anything we can show.
 *
 * Grouped by what the reader can actually do about it, which is the only distinction worth drawing:
 * wait, sign in again, or tell us. Anything finer would be guessing at a cause we do not have —
 * the case this function exists for is precisely the one where the response carried no explanation.
 *
 * Public because `jsTest/ApiErrorMessageTest.kt` asserts every branch and stays in `:site` while
 * this file moves to `:kit`.
 */
fun genericFailure(status: Int): String = when {
    status == 401 || status == 403 -> "You are not signed in, or no longer have access to this."
    status == 404 -> "That is not there any more."
    status == 429 -> "Too many requests just now. Wait a moment and try again."
    status >= 500 -> "Something went wrong on our side. Nothing you typed has been lost — try again."
    else -> "That request didn't go through. Try again."
}
